/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import React, { useState, useEffect } from 'react';
import { ActivityItem, VelocityData } from '@shared/types/dashboard';
import { ActivityCard } from '../components/dashboard/ActivityCard';
import { VelocityGraph } from '../components/dashboard/VelocityGraph';
import { SystemAgentStatus } from '../components/dashboard/SystemAgentStatus';
import { DSAStatus } from '../components/dashboard/DSAStatus';
import { useApp } from '../contexts/AppContext';
import { Home, RefreshCw } from 'lucide-react';
import { getDashboardActivity, getDashboardVelocity } from '../lib/api';

export function Dashboard() {
  const { dashboardSettings } = useApp();
  const [activities, setActivities] = useState<ActivityItem[]>([]);
  const [velocityData, setVelocityData] = useState<VelocityData>({ weeks: [], products: [] });
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  const [isLoadingVelocity, setIsLoadingVelocity] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());

  // Load activity data
  const loadActivity = async () => {
    setIsLoadingActivity(true);
    try {
      const result = await getDashboardActivity(dashboardSettings.activityDays);
      
      if (result.error) {
        console.error('Error loading activity:', result.error);
      }
      
      setActivities(result.activities || []);
      console.log(`Loaded ${result.activities?.length || 0} activities`);
    } catch (error) {
      console.error('Error loading activity:', error);
      setActivities([]);
    } finally {
      setIsLoadingActivity(false);
    }
  };

  // Load velocity data
  const loadVelocity = async () => {
    setIsLoadingVelocity(true);
    try {
      const result = await getDashboardVelocity(dashboardSettings.velocityWeeks);
      
      if (result.error) {
        console.error('Error loading velocity:', result.error);
      }
      
      setVelocityData({
        weeks: result.weeks || [],
        products: result.products || []
      });
      console.log(`Loaded velocity data for ${result.products?.length || 0} products`);
    } catch (error) {
      console.error('Error loading velocity:', error);
      setVelocityData({ weeks: [], products: [] });
    } finally {
      setIsLoadingVelocity(false);
    }
  };

  // Load all data
  const loadDashboardData = async () => {
    setLastRefresh(new Date());
    await Promise.all([loadActivity(), loadVelocity()]);
  };

  // Load on mount and when settings change
  useEffect(() => {
    loadDashboardData();
  }, [dashboardSettings.activityDays, dashboardSettings.velocityWeeks]);

  // Separate activities by type
  const productActivities = activities.filter(a => a.type === 'product');
  const userStoryActivities = activities.filter(a => a.type === 'user-story');
  const issueActivities = activities.filter(a => a.type === 'issue');

  return (
    <div className="container">
      <div className="card">
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          marginBottom: '24px' 
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Home size={24} style={{ marginRight: '12px', color: '#0066cc' }} />
            <div>
              <h2 style={{ margin: 0 }}>Dashboard</h2>
              <p style={{ 
                margin: '4px 0 0 0', 
                fontSize: '14px', 
                color: '#666' 
              }}>
                Activity from last {dashboardSettings.activityDays} days • Velocity for {dashboardSettings.velocityWeeks} weeks
              </p>
            </div>
          </div>
          <button
            className="btn"
            onClick={loadDashboardData}
            disabled={isLoadingActivity || isLoadingVelocity}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <RefreshCw 
              size={16} 
              style={{ 
                animation: (isLoadingActivity || isLoadingVelocity) ? 'spin 1s linear infinite' : 'none'
              }} 
            />
            Refresh
          </button>
        </div>

        {/* Last refresh time */}
        <div style={{ 
          marginBottom: '20px', 
          fontSize: '12px', 
          color: '#999',
          textAlign: 'right'
        }}>
          Last updated: {lastRefresh.toLocaleTimeString()}
        </div>

        {/* Activity Cards */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px',
          marginBottom: '24px'
        }}>
          <ActivityCard
            title="Products"
            icon="product"
            activities={productActivities}
            isLoading={isLoadingActivity}
          />
          <ActivityCard
            title="User Stories"
            icon="user-story"
            activities={userStoryActivities}
            isLoading={isLoadingActivity}
          />
          <ActivityCard
            title="Issues"
            icon="issue"
            activities={issueActivities}
            isLoading={isLoadingActivity}
          />
        </div>

        {/* Velocity Graph and Agents Status */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '2fr 1fr', 
          gap: '20px',
          marginBottom: '24px'
        }}>
          <VelocityGraph 
            data={velocityData}
            isLoading={isLoadingVelocity}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <SystemAgentStatus 
              isLoading={isLoadingActivity || isLoadingVelocity}
            />
            <DSAStatus 
              isLoading={isLoadingActivity || isLoadingVelocity}
            />
          </div>
        </div>

        {/* Info Footer */}
        <div style={{ 
          marginTop: '24px', 
          padding: '16px', 
          background: '#f9f9f9', 
          borderRadius: '8px',
          border: '1px solid #e9e9e9',
          fontSize: '13px',
          color: '#666'
        }}>
          <strong>Note:</strong> Activity and velocity data are computed from git history of markdown files in the repository.
          Changes are tracked for <code style={{ 
            background: '#e9e9e9', 
            padding: '2px 6px', 
            borderRadius: '3px',
            fontSize: '12px'
          }}>data/product/issues.md</code>, <code style={{ 
            background: '#e9e9e9', 
            padding: '2px 6px', 
            borderRadius: '3px',
            fontSize: '12px'
          }}>data/product/products/</code>, and <code style={{ 
            background: '#e9e9e9', 
            padding: '2px 6px', 
            borderRadius: '3px',
            fontSize: '12px'
          }}>data/product/user-stories/</code>.
        </div>
      </div>

      {/* Add CSS for spinner animation */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

